<script setup>
import { computed } from 'vue'
import draggable from 'vuedraggable'
import MediaflowDock from './MediaflowDock.vue'
import MediaflowSourceRow from './MediaflowSourceRow.vue'
import MediaflowDockEmpty from './MediaflowDockEmpty.vue'
import MediaflowSceneLimit from './MediaflowSceneLimit.vue'

/**
 * OBS "Sources" dock: the sources of the scene in Preview, top of the list =
 * front layer (the order the API keeps). Drag (vuedraggable, mouse + touch)
 * and the row menus (keyboard) both emit `reorder(from, to)`.
 */
const props = defineProps({
	flow: { type: Object, default: null },
	maxMedia: { type: Number, default: 0 },
	enabled: { type: Boolean, default: true },
	subtitleFor: { type: Function, default: () => '' },
	previewAvailable: { type: Function, default: () => false },
	selectedMediaId: { type: String, default: null },
	/** mediaId → 'new' | 'changed' | null: staged on the on-air scene, not live yet. */
	draftStateFor: { type: Function, default: () => null },
	/** The scene is on Program: audio changes reach viewers at once (they are never drafted). */
	audioLive: { type: Boolean, default: false },
	/** Pop-out: order, show / hide, audio and position only; adding, editing and removing stay in the main window. */
	readOnly: { type: Boolean, default: false },
})

const emit = defineEmits([
	'add', 'reorder', 'toggle-show', 'toggle-mute', 'volume', 'audio-sync', 'preview', 'edit', 'delete', 'select', 'position',
])

const media = computed(() => (Array.isArray(props.flow?.media) ? props.flow.media : []))
const atLimit = computed(() => props.maxMedia > 0 && media.value.length >= props.maxMedia)
const addReason = computed(() => {
	if (!props.flow) return 'Pick a scene first'
	if (!props.enabled) return 'Enable the production first'
	if (atLimit.value) return 'Upgrade your plan to add more sources'
	return ''
})
const count = computed(() => String(media.value.length))

// vuedraggable needs a writable list; the parent owns the real order.
const localMedia = computed({ get: () => media.value, set: () => {} })
const onDragEnd = ({ oldIndex, newIndex }) => {
	if (oldIndex === undefined || newIndex === undefined || oldIndex === newIndex) return
	emit('reorder', oldIndex, newIndex)
}
const onMove = (index, dir) => {
	const to = index + dir
	if (to < 0 || to >= media.value.length) return
	emit('reorder', index, to)
}
</script>

<template>
	<MediaflowDock title="Sources" :count="count" :count-label="`${media.length} ${media.length === 1 ? 'source' : 'sources'} in this scene`" testid="dock-sources" :add-label="readOnly ? '' : 'Add source'" :add-disabled-reason="addReason" @add="emit('add')">
		<template v-if="flow && maxMedia > 0 && !readOnly" #header-extra>
			<MediaflowSceneLimit :used="media.length" :max="maxMedia" :reached="atLimit" :title="`${media.length} of ${maxMedia} sources in this scene`" testid="dock-sources-usage" />
		</template>
		<template v-if="flow" #context>In Preview: <span class="text-surface-text-1">{{ flow.name }}</span></template>
		<MediaflowDockEmpty v-if="!flow" text="Pick a scene to see its sources." />
		<MediaflowDockEmpty v-else-if="!media.length && readOnly" text="This scene is empty. Add sources in the main window." />
		<MediaflowDockEmpty v-else-if="!media.length" text="This scene is empty." action-label="Add source" :disabled-reason="addReason" @add="emit('add')" />
		<draggable
			v-else
			v-model="localMedia"
			item-key="id"
			tag="ul"
			class="m-0 flex list-none flex-col gap-1.5 p-0"
			handle=".source-drag-handle"
			:disabled="!enabled"
			:animation="160"
			ghost-class="opacity-40"
			@end="onDragEnd"
		>
			<template #item="{ element: item, index: idx }">
				<MediaflowSourceRow
					:media="item"
					:index="idx"
					:count="media.length"
					:enabled="enabled"
					:preview-available="previewAvailable(item)"
					:subtitle="subtitleFor(item)"
					:selected="item.id === selectedMediaId"
					:draft="draftStateFor(item.id)"
					:audio-live="audioLive"
					:read-only="readOnly"
					@select="emit('select', item)"
					@move="onMove(idx, $event)"
					@position="emit('position', item)"
					@toggle-show="emit('toggle-show', item)"
					@toggle-mute="emit('toggle-mute', item)"
					@volume="emit('volume', item, $event)"
					@audio-sync="emit('audio-sync', item, $event)"
					@preview="emit('preview', item)"
					@edit="emit('edit', item)"
					@delete="emit('delete', item)"
				/>
			</template>
		</draggable>
		<template #footer>
			<p v-if="media.length > 1" class="m-0 shrink-0 px-3 pb-2 pt-0.5 text-[0.7rem] text-surface-7">Top is in front. Drag ⠿ to reorder.</p>
		</template>
	</MediaflowDock>
</template>
